"use client"

import { useState, useEffect } from "react"
import { useRouter } from "next/router"
import Link from "next/link"
import { ChevronLeft, ChevronRight, Star } from "lucide-react"

import { Button } from "@/components/ui/button"
import { SessionError, useAuth } from "@/components/auth-provider"

export function LoginFormModern() {
  const router = useRouter()
  const { signInWithGoogle } = useAuth()
  const [returnTo, setReturnTo] = useState<string>("/")
  const [isAuthenticating, setIsAuthenticating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [errorReference, setErrorReference] = useState<string | null>(null)

  // Get the return URL from query parameters. Only paths on this site are
  // accepted, so a crafted link cannot send someone elsewhere after sign-in.
  useEffect(() => {
    const target = new URLSearchParams(window.location.search).get("returnTo")
    setReturnTo(target && target.startsWith("/") && !target.startsWith("//") ? target : "/")
  }, [])

  const handleGoogleSignIn = async () => {
    setIsAuthenticating(true)
    setError(null)
    setErrorReference(null)
    try {
      await signInWithGoogle()
      router.push(returnTo || "/")
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "Sign-in didn't finish. Please try again.")
      setErrorReference(err instanceof SessionError && err.reference ? err.reference : null)
      setIsAuthenticating(false)
    }
  }

  const testimonials = [
    {
      quote:
        "As a startup founder, I needed a professional logo quickly and on a budget. Modulify AI Logo Maker delivered exactly what I needed - a modern, scalable logo that perfectly represents my brand. The AI suggestions were surprisingly on-point!",
      author: "Sarah Martinez",
      title: "Founder & CEO",
      subtitle: "TechStart Solutions",
      stars: 5,
    },
    {
      quote:
        "I was skeptical about AI-generated logos, but Modulify AI Logo Maker changed my mind. The customization options are extensive, and the final result looks like it was designed by a professional agency. Saved us thousands in design costs!",
      author: "David Kim",
      title: "Marketing Director",
      subtitle: "GrowthWave Digital",
      stars: 5,
    },
    {
      quote:
        "What impressed me most about Modulify AI Logo Maker was how it understood our brand's personality. The AI suggested designs that perfectly captured our eco-friendly mission. The process was intuitive and the results were exactly what we needed.",
      author: "Emma Thompson",
      title: "Brand Manager",
      subtitle: "EcoLife Products",
      stars: 5,
    },
  ]

  const [currentTestimonial, setCurrentTestimonial] = useState(0)

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length)
  }

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length)
  }

  const testimonial = testimonials[currentTestimonial]

  return (
    <div className="grid gap-14 lg:grid-cols-2 lg:items-start lg:gap-20">
      {/* Sign in. One button, then straight into the logo maker. */}
      <div className="flex w-full max-w-sm flex-col gap-8">
        <div className="flex flex-col gap-4">
          <h1 className="font-display text-h1 text-balance">Welcome</h1>
          <p className="text-lead text-ink-muted text-pretty">
            One click with the account you already have. No password to invent.
          </p>
        </div>

        <div className="flex flex-col gap-5">
          <Button
            onClick={handleGoogleSignIn}
            disabled={isAuthenticating}
            size="lg"
            className="w-full"
          >
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <path d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.344-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z" />
            </svg>
            {isAuthenticating ? "Signing in..." : "Continue with Google"}
          </Button>

          {error && (
            <div
              role="alert"
              className="flex flex-col gap-2 rounded-md border border-danger/40 bg-danger-soft px-4 py-3 text-small text-danger [&_code]:break-all [&_code]:font-mono [&_code]:text-caption"
            >
              <p>{error}</p>
              {/* No class on the code element, so the reference leads its markup. */}
              {errorReference && <code>{errorReference}</code>}
            </div>
          )}

          <p className="text-small text-ink-muted text-pretty">
            You land back in the logo maker with your drafts intact. Each logo is $7, paid once,
            with three concepts and every file included.
          </p>
        </div>

        <p className="border-t border-line pt-6 text-caption text-ink-subtle text-pretty">
          By continuing, you agree to our{" "}
          <Link
            href="/terms"
            className="text-ink-muted underline decoration-line-strong underline-offset-4 transition-colors duration-fast ease-out hover:text-ink hover:decoration-ink"
          >
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link
            href="/privacy"
            className="text-ink-muted underline decoration-line-strong underline-offset-4 transition-colors duration-fast ease-out hover:text-ink hover:decoration-ink"
          >
            Privacy Policy
          </Link>
          .
        </p>
      </div>

      {/* One quote at a time. No decoration, just the words. */}
      <figure className="flex flex-col gap-6 border-l border-line-strong pl-6 sm:pl-8">
        <span
          className="flex items-center gap-1"
          role="img"
          aria-label={`Rated ${testimonial.stars} out of 5`}
        >
          {Array.from({ length: testimonial.stars }).map((_, index) => (
            <Star key={index} className="size-4 fill-ink text-ink" aria-hidden />
          ))}
        </span>

        <blockquote className="font-display text-h3 text-balance">
          {testimonial.quote}
        </blockquote>

        <figcaption className="flex flex-col">
          <span className="text-small font-medium text-ink">{testimonial.author}</span>
          <span className="text-small text-ink-muted">
            {testimonial.title}, {testimonial.subtitle}
          </span>
        </figcaption>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon-sm"
            onClick={prevTestimonial}
            aria-label="Previous testimonial"
          >
            <ChevronLeft aria-hidden />
          </Button>
          <Button
            variant="outline"
            size="icon-sm"
            onClick={nextTestimonial}
            aria-label="Next testimonial"
          >
            <ChevronRight aria-hidden />
          </Button>
          <span className="ml-2 text-caption tabular-nums text-ink-subtle">
            {currentTestimonial + 1} of {testimonials.length}
          </span>
        </div>
      </figure>
    </div>
  )
}
